import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Snackbar,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
  Grid
} from '@mui/material';
import {
  Search as SearchIcon,
  Language as DomainIcon,
  ContactPage as ContactPageIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  LinkedIn as LinkedInIcon,
  ContentCopy as CopyIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import api from '../api';
import logger from '../utils/logger';
import useIsMobile from '../hooks/useIsMobile';
import useSearchHistory from '../hooks/useSearchHistory';
import SearchHistoryPanel from './SearchHistoryPanel';

const UnifiedSearchSection = ({ 
  onDomainSearch, 
  onContactSearchResult,
  loading: externalLoading 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'error'
  });
  
  // Domain search state
  const [domainSearch, setDomainSearch] = useState('');
  
  // Contact search states
  const [contactActiveTab, setContactActiveTab] = useState(0);
  const [contactData, setContactData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyDomain: '',
    linkedinUrl: ''
  });
  const [contactResult, setContactResult] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  const isMobile = useIsMobile();
  const {
    domainHistory,
    contactHistory,
    addDomainSearch,
    addContactSearch,
    removeDomain,
    removeContact,
    clearDomainHistory,
    clearContactHistory,
  } = useSearchHistory();

  const formRef = useRef(null);

  const showNotification = (message, severity = 'error') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setContactResult(null);
    setCreditInfo(null);
  };

  const handleContactTabChange = (event, newValue) => {
    setContactActiveTab(newValue);
    setError('');
    setContactResult(null);
  };

  const handleContactInputChange = (field) => (event) => {
    setContactData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  // Copy function for contact results
  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(`${label} copied to clipboard!`);
      setTimeout(() => setCopyFeedback(''), 3000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setCopyFeedback('Failed to copy to clipboard');
      setTimeout(() => setCopyFeedback(''), 3000);
    }
  };

  const handleCopyAllContactInfo = async () => {
    const personData = formatPersonData(contactResult);
    if (!personData) return;

    const locationText = personData.location 
      ? typeof personData.location === 'string' 
        ? personData.location 
        : `${personData.location.city || ''}, ${personData.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') || 'Location available'
      : 'N/A';

    const contactInfo = [
      `Name: ${personData.name || 'N/A'}`,
      `Job Title: ${personData.jobTitle || 'N/A'}`,
      `Company: ${personData.company || 'N/A'}`,
      personData.emails?.length > 0 ? `Emails: ${personData.emails.map(e => e.email).join(', ')}` : 'Emails: N/A',
      personData.phones?.length > 0 ? `Phones: ${personData.phones.map(p => p.number).join(', ')}` : 'Phones: N/A',
      personData.linkedin ? `LinkedIn: ${personData.linkedin}` : 'LinkedIn: N/A',
      `Location: ${locationText}`
    ].join('\n');

    await handleCopy(contactInfo, 'All contact info');
  };

  // Format person data from API response
  const formatPersonData = (data) => {
    if (!data || !data.data) return null;

    const person = data.data;
    return {
      name: person.fullName || `${person.firstName || ''} ${person.lastName || ''}`.trim(),
      jobTitle: person.jobTitle?.title || person.jobTitle,
      company: person.company?.name || person.companyName,
      emails: person.emailAddresses || [],
      phones: person.phoneNumbers || [],
      linkedin: person.socialLinks?.linkedin,
      location: person.location,
      companyInfo: person.company,
      searchMethod: data.search_method,
      creditsCharged: data.credits_charged
    };
  };

  // Render contact search results
  const renderPersonResult = () => {
    const personData = formatPersonData(contactResult);
    if (!personData) return null;

    return (
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Avatar sx={{ width: 60, height: 60, mr: 2, bgcolor: 'primary.main' }}>
              {personData.name ? personData.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'N/A'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mr: 1 }}>
                  {personData.name || 'N/A'}
                </Typography>
                {personData.name && personData.name !== 'N/A' && (
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopy(personData.name, 'Name')}
                    sx={{ ml: 0.5 }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mr: 1 }}>
                  {personData.jobTitle || 'N/A'}
                </Typography>
                {personData.jobTitle && personData.jobTitle !== 'N/A' && (
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopy(personData.jobTitle, 'Job title')}
                    sx={{ ml: 0.5 }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  {personData.company || 'N/A'}
                </Typography>
                {personData.company && personData.company !== 'N/A' && (
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopy(personData.company, 'Company')}
                    sx={{ ml: 0.5 }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Contact Information
          </Typography>

          <Grid container spacing={2}>
            {/* Email addresses */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Email Addresses
                </Typography>
              </Box>
              {personData.emails && personData.emails.length > 0 ? (
                personData.emails.map((email, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      component="a"
                      href={`mailto:${email.email}`}
                      sx={{ mr: 1, color: 'primary.main', textDecoration: 'none' }}
                    >
                      {email.email}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopy(email.email, 'Email')}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                    {email.type && (
                      <Chip 
                        label={email.type} 
                        size="small" 
                        variant="outlined" 
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No email addresses found
                </Typography>
              )}
            </Grid>

            {/* Phone numbers */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Phone Numbers
                </Typography>
              </Box>
              {personData.phones && personData.phones.length > 0 ? (
                personData.phones.map((phone, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      component="a"
                      href={`tel:${phone.number.replace(/\s/g, '')}`}
                      sx={{ mr: 1, color: 'primary.main', textDecoration: 'none' }}
                    >
                      {phone.number}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => handleCopy(phone.number, 'Phone')}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                    {phone.type && (
                      <Chip 
                        label={phone.type} 
                        size="small" 
                        variant="outlined" 
                        sx={{ ml: 1, height: 20 }}
                      />
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No phone numbers found
                </Typography>
              )}
            </Grid>

            {/* LinkedIn */}
            {personData.linkedin && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LinkedInIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    LinkedIn Profile
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    variant="body2" 
                    component="a" 
                    href={personData.linkedin}
                    target="_blank"
                    sx={{ mr: 1, color: 'primary.main', textDecoration: 'none' }}
                  >
                    {personData.linkedin}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopy(personData.linkedin, 'LinkedIn URL')}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Location */}
            {personData.location && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Location
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {typeof personData.location === 'string' 
                      ? personData.location 
                      : `${personData.location.city || ''}, ${personData.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') || 'Location available'
                    }
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => handleCopy(
                      typeof personData.location === 'string' 
                        ? personData.location 
                        : `${personData.location.city || ''}, ${personData.location.country || ''}`.replace(/^,\s*|,\s*$/g, '') || 'Location available',
                      'Location'
                    )}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CopyIcon />}
              onClick={handleCopyAllContactInfo}
            >
              Copy All Info
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip 
                label={`Search Method: ${personData.searchMethod || 'Unknown'}`}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
            </Box>
          </Box>

          {copyFeedback && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {copyFeedback}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // Domain search functionality (from Header.jsx)
  const formatUrl = (url) => {
    if (!url) return url;
    
    url = url.trim();
    
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    try {
      const urlObj = new URL(url);
      return urlObj.href;
    } catch (e) {
      logger.error('Invalid URL format:', url);
      return url;
    }
  };

  const extractDomainFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.toLowerCase();
    } catch (e) {
      logger.error('Failed to extract domain from URL:', url);
      return url.replace(/^https?:\/\//, '').split('/')[0].toLowerCase();
    }
  };

  const extractCompanyNameFromUrl = (url) => {
    try {
      const domain = extractDomainFromUrl(url);
      const parts = domain.split('.');
      if (parts.length >= 2) {
        return parts[parts.length - 2].charAt(0).toUpperCase() + parts[parts.length - 2].slice(1);
      }
      return domain;
    } catch (e) {
      logger.error('Failed to extract company name from URL:', url);
      return 'Unknown Company';
    }
  };

  const isDomainValid = (dnsCheckResult) => {
    if (!dnsCheckResult || !dnsCheckResult.success) {
      logger.error('DNS check failed or invalid response', dnsCheckResult);
      return false;
    }
    
    // First priority: Use the domain_exists property if available
    if (dnsCheckResult.data && dnsCheckResult.data.domain_exists === false) {
      logger.log('Domain does not exist according to domain_exists property');
      return false;
    }
    
    if (dnsCheckResult.data && dnsCheckResult.data.domain_exists === true) {
      logger.log('Domain exists according to domain_exists property');
      return true;
    }
    
    // Check for explicit "does not exist" errors
    const errors = dnsCheckResult.data?.errors || [];
    if (errors.length > 0) {
      logger.log('DNS check errors:', errors);
      const hasNonExistenceError = errors.some(error => 
        typeof error === 'string' && error.toLowerCase().includes('does not exist')
      );
      
      if (hasNonExistenceError) {
        logger.log('Domain does not exist according to DNS errors');
        return false;
      }
    }
    
    // Get the data object from the result
    const data = dnsCheckResult.data || {};
    logger.log('DNS data for indicators check:', data);
    
    // Check various indicators from DNS results that suggest the domain exists
    const indicators = [
      // Check MX records
      data.email && Array.isArray(data.email.records) && data.email.records.length > 0,
      
      // Check for SPF records
      data.spf && data.spf.found === true,
      
      // Check for DMARC records
      data.dmarc && data.dmarc.found === true,
      
      // Check for DKIM records
      data.dkim && data.dkim.found === true,
      
      // Check for Microsoft verification
      data.microsoft && data.microsoft.found === true,
      
      // Check for Azure enrollment
      data.azure && data.azure.found === true,
      
      // Check for Enterprise enrollment
      data.enterprise && data.enterprise.found === true,
      
      // Check for verification records
      data.verifications && data.verifications.found === true
    ];
    
    // Log the indicators for debugging
    logger.log('Domain existence indicators:', indicators);
    
    // If any of these indicators are true, domain likely exists
    const exists = indicators.some(Boolean);
    logger.log('Final domain existence determination:', exists);
    
    return exists;
  };

  const handleDomainHistorySelect = async (entry) => {
    setActiveTab(0);
    setLoading(true);
    setError('');

    try {
      let record = entry.record;
      if (entry.url) {
        const urlCheckResult = await api.checkUrlInRecords(entry.url);
        if (urlCheckResult.success && urlCheckResult.found && urlCheckResult.records.length > 0) {
          record = urlCheckResult.records[0];
        }
      }

      addDomainSearch(record);

      if (onDomainSearch) {
        onDomainSearch(record);
      }
    } catch (err) {
      logger.error('Error reopening domain from history:', err);
      if (onDomainSearch && entry.record) {
        onDomainSearch(entry.record);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactHistorySelect = (entry) => {
    setError('');
    setCreditInfo(null);
    setCopyFeedback('');
    setContactResult(entry.response);
    setActiveTab(1);
    addContactSearch(entry.response);

    if (onContactSearchResult) {
      onContactSearchResult(entry.response);
    }
  };

  const handleDomainSubmit = async (event) => {
    event.preventDefault();
    
    if (!domainSearch.trim()) {
      setError('Please enter a domain name');
      return;
    }

    const urls = domainSearch.split(',').map(url => url.trim()).filter(url => url);
    if (urls.length > 0) {
      logger.log('Processing URL for SalesOutreach:', urls[0]);
      
      const url = urls[0];
      let cleanUrl = formatUrl(url);
      let domain = extractDomainFromUrl(cleanUrl);
      
      if (!domain || domain.length === 0 || !domain.includes('.')) {
        logger.error(`Invalid domain format: ${url}`);
        showNotification(`Invalid domain format: ${url}. Please enter a valid domain.`, 'error');
        return;
      }

      const companyName = extractCompanyNameFromUrl(cleanUrl);
      logger.log(`Extracted company name: ${companyName}`);
      
      setLoading(true);
      setError('');
      
      try {
        logger.log(`Checking if domain exists: ${domain}`);
        const dnsCheckResult = await api.checkDomainDNS(domain, true);
        
        const domainExists = isDomainValid(dnsCheckResult);
        
        if (!domainExists) {
          logger.error(`Domain does not exist: ${domain}`);
          showNotification(`Domain "${domain}" does not exist or is not accessible.`, 'error');
          setLoading(false);
          setDomainSearch('');
          return;
        }
        
        logger.log(`Checking if URL exists in records: ${cleanUrl} (Domain: ${domain})`);
        const urlCheckResult = await api.checkUrlInRecords(cleanUrl);
        
        let record;
        
        if (urlCheckResult.success && urlCheckResult.found && urlCheckResult.records.length > 0) {
          logger.log(`Found ${urlCheckResult.count} existing records for URL: ${cleanUrl}`);
          record = urlCheckResult.records[0];
        } else {
          logger.log(`No existing records found for URL: ${cleanUrl}, creating a new record`);
          
          record = {
            Domain: domain,
            Instellingsnaam: companyName,
            Telefoonnummer: '-',
            URL: cleanUrl,
            'Vestigingsadres Plaats': '-',
            Website: cleanUrl,
            record_id: crypto.randomUUID()
          };
          
          try {
            const addResult = await api.addRecord(record);
            if (addResult.success) {
              record.record_id = addResult.record_id;
              logger.log(`Created new record with ID: ${record.record_id}`);
            }
          } catch (addErr) {
            logger.error('Error adding record:', addErr);
          }
        }
        
        try {
          await api.logUrlSearch(companyName, cleanUrl, domain);
          logger.log('URL search activity logged successfully');
        } catch (logError) {
          logger.error('Failed to log URL search activity:', logError);
        }
        
        // Call the callback to handle the domain search result
        addDomainSearch(record);

        if (onDomainSearch) {
          onDomainSearch(record);
        }
        
        setDomainSearch('');
        showNotification('Domain search completed successfully!', 'success');
        
      } catch (err) {
        logger.error('Error during URL search:', err);
        showNotification(`Error processing URL: ${err.message || 'Unknown error'}`, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  // Contact search functionality (from LushaPersonSearchModal.jsx)
  const searchByEmail = async () => {
    if (!contactData.email) {
      setError('Email address is required');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/api/lusha/person/search-by-email', {
        email: contactData.email
      });

      if (response.data.success) {
        setContactResult(response.data);
        if (response.data.data) {
          addContactSearch(response.data);
        }
        setCreditInfo({
          charged: response.data.credits_charged || false,
          used: response.data.credits_charged ? 1 : 0,
          method: 'email',
          searchTerm: contactData.email
        });
        
        if (onContactSearchResult) {
          onContactSearchResult(response.data);
        }
        
        showNotification('Contact search completed successfully!', 'success');
      } else {
        setError(response.data.error || 'No person found for this email');
        setCreditInfo({
          charged: response.data.credits_charged || false,
          used: 0,
          method: 'email',
          searchTerm: contactData.email
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search for person');
      setCreditInfo({
        charged: false,
        used: 0,
        method: 'email',
        searchTerm: contactData.email
      });
    } finally {
      setLoading(false);
    }
  };

  const searchByName = async () => {
    if (!contactData.firstName || !contactData.lastName) {
      setError('First name and last name are required');
      return;
    }

    if (!contactData.companyName && !contactData.companyDomain) {
      setError('Either company name or company domain is required');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/api/lusha/person/search-by-name', {
        first_name: contactData.firstName,
        last_name: contactData.lastName,
        company_name: contactData.companyName || undefined,
        company_domain: contactData.companyDomain || undefined
      });

      if (response.data.success) {
        setContactResult(response.data);
        if (response.data.data) {
          addContactSearch(response.data);
        }
        setCreditInfo({
          charged: response.data.credits_charged || false,
          used: response.data.credits_charged ? 1 : 0,
          method: 'name',
          searchTerm: `${contactData.firstName} ${contactData.lastName}`
        });
        
        if (onContactSearchResult) {
          onContactSearchResult(response.data);
        }
        
        showNotification('Contact search completed successfully!', 'success');
      } else {
        setError(response.data.error || 'No person found with these details');
        setCreditInfo({
          charged: response.data.credits_charged || false,
          used: 0,
          method: 'name',
          searchTerm: `${contactData.firstName} ${contactData.lastName}`
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search for person');
      setCreditInfo({
        charged: false,
        used: 0,
        method: 'name',
        searchTerm: `${contactData.firstName} ${contactData.lastName}`
      });
    } finally {
      setLoading(false);
    }
  };

  const searchByLinkedIn = async () => {
    if (!contactData.linkedinUrl) {
      setError('LinkedIn URL is required');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/api/lusha/person/search-by-linkedin', {
        linkedin_url: contactData.linkedinUrl
      });

      if (response.data.success) {
        setContactResult(response.data);
        if (response.data.data) {
          addContactSearch(response.data);
        }
        setCreditInfo({
          charged: response.data.credits_charged || false,
          used: response.data.credits_charged ? 1 : 0,
          method: 'linkedin',
          searchTerm: contactData.linkedinUrl
        });
        
        if (onContactSearchResult) {
          onContactSearchResult(response.data);
        }
        
        showNotification('Contact search completed successfully!', 'success');
      } else {
        setError(response.data.error || 'No person found for this LinkedIn URL');
        setCreditInfo({
          charged: response.data.credits_charged || false,
          used: 0,
          method: 'linkedin',
          searchTerm: contactData.linkedinUrl
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to search for person');
      setCreditInfo({
        charged: false,
        used: 0,
        method: 'linkedin',
        searchTerm: contactData.linkedinUrl
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContactSearch = () => {
    switch (contactActiveTab) {
      case 0:
        searchByEmail();
        break;
      case 1:
        searchByName();
        break;
      case 2:
        searchByLinkedIn();
        break;
      default:
        break;
    }
  };

  const renderDomainSearch = () => (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search for company information by entering a domain name
      </Typography>
      
      <Paper
        component="form"
        onSubmit={handleDomainSubmit}
        ref={formRef}
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          p: 1,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <TextField
          placeholder="Enter domain name (e.g., example.com)"
          value={domainSearch}
          onChange={(e) => setDomainSearch(e.target.value)}
          variant="standard"
          fullWidth
          InputProps={{
            disableUnderline: true,
          }}
          sx={{ ml: 1, flex: 1 }}
        />
        <IconButton 
          type="submit"
          sx={{ p: 1 }}
          disabled={loading || externalLoading}
        >
          {loading || externalLoading ? (
            <CircularProgress size={20} />
          ) : (
            <SearchIcon />
          )}
        </IconButton>
      </Paper>

      {!isMobile && (
        <SearchHistoryPanel
          title="Recent domain searches"
          items={domainHistory}
          type="domain"
          pageSize={5}
          onSelect={handleDomainHistorySelect}
          onRemove={removeDomain}
          onClear={clearDomainHistory}
        />
      )}
    </Box>
  );

  const renderContactEmailTab = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the email address of the person you want to find
      </Typography>
      
      <TextField
        fullWidth
        label="Email Address"
        placeholder="john.doe@company.com"
        value={contactData.email}
        onChange={handleContactInputChange('email')}
        type="email"
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
      />
      
      <Button
        variant="contained"
        onClick={handleContactSearch}
        disabled={loading || !contactData.email}
        startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
        fullWidth
      >
        {loading ? 'Searching...' : 'Search Person'}
      </Button>
    </Box>
  );

  const renderContactNameTab = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the person's name and company information
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2 }}>
        <TextField
          label="First Name"
          placeholder="John"
          value={contactData.firstName}
          onChange={handleContactInputChange('firstName')}
          variant="outlined"
          size="small"
          fullWidth
        />
        <TextField
          label="Last Name"
          placeholder="Doe"
          value={contactData.lastName}
          onChange={handleContactInputChange('lastName')}
          variant="outlined"
          size="small"
          fullWidth
        />
      </Box>
      
      <TextField
        fullWidth
        label="Company Name"
        placeholder="Acme Corp"
        value={contactData.companyName}
        onChange={handleContactInputChange('companyName')}
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
      />
      
      <TextField
        fullWidth
        label="Company Domain (optional)"
        placeholder="acme.com"
        value={contactData.companyDomain}
        onChange={handleContactInputChange('companyDomain')}
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
      />
      
      <Button
        variant="contained"
        onClick={handleContactSearch}
        disabled={loading || !contactData.firstName || !contactData.lastName || (!contactData.companyName && !contactData.companyDomain)}
        startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
        fullWidth
      >
        {loading ? 'Searching...' : 'Search Person'}
      </Button>
    </Box>
  );

  const renderContactLinkedInTab = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the LinkedIn profile URL of the person you want to find
      </Typography>
      
      <TextField
        fullWidth
        label="LinkedIn URL"
        placeholder="https://www.linkedin.com/in/johndoe"
        value={contactData.linkedinUrl}
        onChange={handleContactInputChange('linkedinUrl')}
        variant="outlined"
        size="small"
        sx={{ mb: 2 }}
      />
      
      <Button
        variant="contained"
        onClick={handleContactSearch}
        disabled={loading || !contactData.linkedinUrl}
        startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
        fullWidth
      >
        {loading ? 'Searching...' : 'Search Person'}
      </Button>
    </Box>
  );

  const renderContactSearch = () => (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search for a person using their email, name + company, or LinkedIn profile
      </Typography>

      <Tabs
        value={contactActiveTab}
        onChange={handleContactTabChange}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons={isMobile ? 'auto' : false}
        sx={{ mb: 2 }}
      >
        <Tab icon={<EmailIcon />} label={isMobile ? 'Email' : 'By Email'} iconPosition="start" />
        <Tab icon={<PersonIcon />} label={isMobile ? 'Name' : 'By Name'} iconPosition="start" />
        <Tab icon={<LinkedInIcon />} label={isMobile ? 'LinkedIn' : 'By LinkedIn'} iconPosition="start" />
      </Tabs>

      {contactActiveTab === 0 && renderContactEmailTab()}
      {contactActiveTab === 1 && renderContactNameTab()}
      {contactActiveTab === 2 && renderContactLinkedInTab()}

      {!isMobile && (
        <SearchHistoryPanel
          title="Recent enriched contacts"
          items={contactHistory}
          type="contact"
          onSelect={handleContactHistorySelect}
          onRemove={removeContact}
          onClear={clearContactHistory}
        />
      )}

      {contactResult && !error && renderPersonResult()}

      {contactResult && !contactResult.data && !error && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No person found with the provided search criteria.
        </Alert>
      )}
    </Box>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 500,
              minHeight: { xs: 48, sm: 64 },
              px: { xs: 1, sm: 2 }
            }
          }}
        >
          <Tab 
            icon={<DomainIcon />} 
            label={isMobile ? 'Domain' : 'Domain Search'}
            iconPosition="start"
          />
          <Tab 
            icon={<ContactPageIcon />} 
            label={isMobile ? 'Contact' : 'Contact Search'}
            iconPosition="start"
          />
        </Tabs>

        {activeTab === 0 && renderDomainSearch()}
        {activeTab === 1 && renderContactSearch()}

        {isMobile && (
          <Box
            sx={{
              px: 2,
              pb: 2,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <SearchHistoryPanel
              title="Recent domain searches"
              items={domainHistory}
              type="domain"
              pageSize={5}
              onSelect={handleDomainHistorySelect}
              onRemove={removeDomain}
              onClear={clearDomainHistory}
              showWhenEmpty
              emptyMessage="Your recent domain searches will appear here"
            />
            <SearchHistoryPanel
              title="Recent enriched contacts"
              items={contactHistory}
              type="contact"
              onSelect={handleContactHistorySelect}
              onRemove={removeContact}
              onClear={clearContactHistory}
              showWhenEmpty
              emptyMessage="Your recent contact searches will appear here"
            />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3, pt: 0 }}>
            <Alert severity="error">
              {error}
            </Alert>
          </Box>
        )}

        {creditInfo && (
          <Box sx={{ p: 3, pt: 0 }}>
            <Alert severity={creditInfo.charged ? 'warning' : 'success'}>
              {creditInfo.charged 
                ? `This search consumed ${creditInfo.used} contact credit(s).`
                : 'This search did not consume any contact credits.'
              }
            </Alert>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={isMobile ? { vertical: 'bottom', horizontal: 'center' } : { vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnifiedSearchSection;
